import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import NewSidebar from "./Sidebar/NewSidebar";
import "./NewappLayout.scss";
import { Snackbar, Alert } from "@mui/material";
import { clearErrors } from "../../Redux/ActionCreators/errorAction";
import { useSelector, useDispatch } from "react-redux";
import useMessengerSocket from "../../Components/Chat/useMessengerSocket";
import messengerSocket from "../../utils/messengerSocket";
import {
  SETTINGS_PAGE_READ_PERMISSION,
  canOpenSettingsPage,
  getMemberMenuRules,
  getPrimaryCompanyUser,
} from "../../constants/rbac";

const AppLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    window.innerWidth < 767
  );
  const [notification, setNotification] = useState(null);
  const [notificationTrigger, setNotificationTrigger] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };
  const error = useSelector((state) => state.error);
  const dispatch = useDispatch();
  // The app socket (messenger + notifications) runs for every mainLayout page.
  // It lives here rather than in NewSidebar because the sidebar unmounts on
  // some routes (billing, mobile create-job) and whenever the mobile keyboard
  // opens.
  useMessengerSocket();

  const snackClosed = () => {
    dispatch(clearErrors());
  };

  const location = useLocation();
  const navigate = useNavigate();
  const meDetails = useSelector((state) => state.auth?.meDetails);

  // Settings pages opened by URL need the same Read permission as their menu item
  useEffect(() => {
    const details = JSON.parse(localStorage.getItem("profileDetails") || "null");
    const { permissionCodes } = getMemberMenuRules({
      companyUser: getPrimaryCompanyUser(meDetails),
      isSuperAdmin: details?.roleId === "1",
    });
    if (canOpenSettingsPage(permissionCodes, location.pathname)) return;
    const fallback = Object.keys(SETTINGS_PAGE_READ_PERMISSION).find((path) =>
      canOpenSettingsPage(permissionCodes, path)
    );
    navigate(fallback || "/jobs", { replace: true });
  }, [meDetails, location.pathname, navigate]);

  // Tenant notifications and resume-upload events come over the shared socket
  useEffect(() => {
    const tenantId = localStorage.getItem('tenentId');
    if (tenantId) messengerSocket.joinGroup(`tenant-${tenantId}`);

    setWsConnected(messengerSocket.isOpen());
    const offOpen = messengerSocket.addListener('open', () => setWsConnected(true));
    const offClose = messengerSocket.addListener('close', () => setWsConnected(false));

    const offMessage = messengerSocket.addListener('message', (parsed) => {
      if (parsed.type === 'notification') {
        // toast.success("You got a new notification", { autoClose: 3000 });
        setNotificationTrigger(prev => !prev);
      }

      if (parsed.event === 'resume_upload_updated' && parsed.data) {
        window.dispatchEvent(new CustomEvent('resume_upload_updated', { detail: parsed.data }));
      }
    });

    return () => {
      offOpen();
      offClose();
      offMessage();
    };
  }, []);

  const disconnectWs = () => {
    messengerSocket.disconnect();
  };

  const isMobile = window.innerWidth < 767;
  const isCreateJobRoute = location.pathname === '/jobs/createjob';
  const isBillingRoute = location.pathname === '/billing/success' || location.pathname === '/billing/cancel';

  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isMobile || typeof window.visualViewport === 'undefined') return;
    const handler = () => {
      const heightDiff = window.innerHeight - window.visualViewport.height;
      setKeyboardOpen(heightDiff > 100);
    };
    window.visualViewport.addEventListener('resize', handler);
    return () => window.visualViewport.removeEventListener('resize', handler);
  }, [isMobile]);

  return (
    <div className="app-layout">
      {!isMobile && !isBillingRoute && (
      <NewSidebar
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        wsConnected={wsConnected}
        disconnectWs={disconnectWs}
      />
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={error.successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert onClose={snackClosed} severity="error" sx={{ width: "100%" }}>
          {error.successMessageText}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={error.successEnable}
        onClose={() => snackClosed()}
        autoHideDuration={3000}
      >
        <Alert onClose={snackClosed} severity="success" sx={{ width: "100%" }}>
          {error.successMessageText}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(notification)}
        autoHideDuration={3000}
        onClose={() => setNotification(null)}
      >
        <Alert onClose={() => setNotification(null)} severity="info" sx={{ width: '100%' }}>
          {notification}
        </Alert>
      </Snackbar>
      <div className="main-content">
        <div className="page-content">
          <Outlet context={{ notificationTrigger }} />
        </div>
        {isMobile && !isCreateJobRoute && !isBillingRoute && !keyboardOpen && (
          <div className="mobile-sidebar">
            <NewSidebar
              isCollapsed={false}
              toggleSidebar={toggleSidebar}
              wsConnected={wsConnected}
              disconnectWs={disconnectWs}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AppLayout;
